<template>
  <div class="flex h-full flex-col bg-white">
    <div class="flex items-center justify-between px-8 py-4" style="background-color: #E2E8F0;">
      <div class="text-lg font-semibold uppercase text-black">
        {{ metric.name }}
      </div>
      <div class="text-slate-500 transition hover:text-slate-700 cursor-pointer" @click="$emit('close')">
        <Icon icon-name="CloseIcon" />
      </div>
    </div>
    <div class="flex-1 space-y-8 bg-white py-6">

      <div class="rounded-lg border border-slate-200 relative mx-8">
        <div
          class="absolute badge-dot inline-flex items-center bg-gray-600 border border-slate-500 px-5 py-2 text-xs font-semibold text-white">
          <span>Storage</span>
        </div>
        <div class="mb-8">
          <div class="flex flex-wrap items-center justify-between gap-3 px-8 mt-8">
            <div class="w-full">
              <ProgressBar
                :value="progressValue"
                :label="progressLabel"
                :primary-percent="progressPrimaryPercent"
                :secondary-percent="progressSecondaryPercent"
                primary-color="#F59E0B"
                secondary-color="#E2E8F0"
              />
            </div>
            <h3 class="text-sm font-semibold text-slate-800 mt-4">Summary</h3>
          </div>
          <div class="mx-8">
            <div
              class="grid grid-cols-3 gap-4 px-6 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600"
            >
              <span>Status</span>
              <span>Date and Time</span>
              <span>Size</span>
            </div>
            <div class="bg-white">
              <div
                v-for="detail in summaryDetails"
                :key="detail.id"
                class="text-xs relative grid grid-cols-3 items-center gap-4 px-6 py-4 mb-2 bg-gray-100 rounded-lg font-semibold text-neutral-800"
              >
                <span :class="detail.accent" class="absolute left-0 top-0 h-full w-1 rounded-l"></span>

                <div>
                  <span>{{ detail.status }}</span>
                </div>

                <div>
                  {{ detail.date }}
                </div>
                <div>
                  {{ detail.size }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
          <div class="flex items-center justify-between px-8 py-4" style="background-color: #E2E8F0;">
      <div class="text-lg font-medium text-gray-900">
        Categories
      </div>
    </div>
         <div class="rounded-lg border border-slate-200 relative mx-8">
        <div
          class="absolute badge-dot inline-flex items-center bg-gray-600 border border-slate-500 px-5 py-2 text-xs font-semibold text-white">
          <span>Storage Used By categories</span>
        </div>
        <div class="mb-8">
          <div class="flex flex-wrap items-center justify-between gap-3 px-8 mt-8">
            <div class="w-full">
              <ProgressBar
                :value="progressValue"
                :label="progressLabel"
                :primary-percent="progressPrimaryPercent"
                :secondary-percent="progressSecondaryPercent"
                primary-color="#F59E0B"
                secondary-color="#E2E8F0"
              />
            </div>
          </div>
          <div class="mx-8" v-if="hasCategories">
            <div
              class="grid grid-cols-3 gap-4 px-6 py-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600"
            >
              <span>Categories</span>
              <span>Storage Usage</span>
              <span>Usage Representation</span>
            </div>
            <div class="bg-white">
              <div
                v-for="category in categoryDetails"
                :key="category.id"
                class="relative grid grid-cols-3 items-center gap-4 px-6 py-4 mb-2 rounded-lg bg-slate-50 text-sm font-medium text-slate-800"
              >
                <span :class="category.accent" class="absolute left-0 top-0 h-full w-1 rounded-l"></span>


                <div>
                  <span>{{ category.name }}</span>
                </div>


                <div class="flex items-baseline gap-1 text-slate-900">
                  <span class="text-base font-semibold">{{ category.used }}</span>
                  <span class="text-xs font-semibold uppercase">/{{ category.total }} {{ category.unit }}</span>
                </div>
                <div>
                  <div class="h-3 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      class="h-full rounded-full"
                      :style="{
                        width: `${category.percent}%`,
                        backgroundColor: category.color
                      }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mx-8" v-else>
            <div class="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
              No storage usage categories available.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import Icon from "../core/Icon.vue";
import ProgressBar from "../core/ProgressBar.vue";
export default {
  name: "StorageDrawerContent",
  components: {
    Icon,
    ProgressBar
  },
  data() {
    return {
      summaryDetails: [],
      categoryDetails: []
    }
  },
  props: {
    metric: {
      type: Object,
      default: null
    }
  },
  computed: {
    tenantBasicInfo() {
      return this.$store?.state?.TenantAnalyticsStore?.tenantBasicInfo || null;
    },
    storageUsage() {
      return this.tenantBasicInfo?.storageUsage || null;
    },
    storageDetail() {
      const details = this.storageUsage?.details;
      if (Array.isArray(details) && details.length > 0) {
        return details[0];
      }
      if (details && typeof details === "object") {
        return details;
      }
      return null;
    },
    hasCategories() {
      return Array.isArray(this.categoryDetails) && this.categoryDetails.length > 0;
    },
    progressValue() {
      return this.storageTotals.usedDisplay;
    },
    progressLabel() {
      return this.storageTotals.label;
    },
    progressPrimaryPercent() {
      return this.storageTotals.primaryPercent;
    },
    progressSecondaryPercent() {
      return this.storageTotals.secondaryPercent;
    },
    storageTotals() {
      const detail = this.storageDetail;
      if (detail) {
        const usedBytes = Number(detail.value) || 0;
        const totalBytes = Number(
          detail.limit ?? detail.total ?? detail.capacity ?? detail.max ?? usedBytes
        ) || 0;

        const usedFormatted = this.formatBytes(usedBytes);
        const totalFormatted = this.formatBytes(
          totalBytes || usedBytes,
          usedFormatted.unitIndex
        );

        const percent = totalBytes > 0
          ? (usedBytes / totalBytes) * 100
          : usedBytes > 0
            ? 100
            : 0;
        const primaryPercent = Math.max(0, Math.min(100, Math.round(percent)));

        return {
          usedDisplay: usedFormatted.value,
          label: `/${totalFormatted.value} ${totalFormatted.unit}`,
          primaryPercent,
          secondaryPercent: Math.max(0, 100 - primaryPercent),
          used: usedBytes,
          total: totalBytes || usedBytes
        };
      }

      const metricValue = Number(this.metric?.value) || 0;
      const metricTotalRaw = Number(this.metric?.total);
      const metricTotal = Number.isFinite(metricTotalRaw) && metricTotalRaw > 0
        ? metricTotalRaw
        : metricValue;
      const percent = metricTotal > 0
        ? (metricValue / metricTotal) * 100
        : metricValue > 0
          ? 100
          : 0;
      const primaryPercent = Math.max(0, Math.min(100, Math.round(percent)));

      return {
        usedDisplay: metricValue,
        label: this.metric?.secondary || (metricTotal ? `/${metricTotal}` : ""),
        primaryPercent,
        secondaryPercent: Math.max(0, 100 - primaryPercent),
        used: metricValue,
        total: metricTotal
      };
    }
  },
  watch: {
    storageUsage: {
      immediate: true,
      handler() {
        this.summaryDetails = this.buildSummaryDetails();
        this.categoryDetails = this.buildCategoryDetails();
      }
    }
  },
  methods: {
    buildSummaryDetails() {
      if (!this.storageUsage || typeof this.storageUsage !== "object") {
        return [];
      }

      const detail = this.storageDetail;
      if (!detail || typeof detail !== "object") {
        if (this.metric) {
          const sizeLabel = (this.metric.secondary || "").replace(/^\//, "").trim();
          return [
            {
              id: "storage-metric",
              status: this.metric.name || "Storage",
              date: "-",
              size: sizeLabel || `${this.metric.value ?? 0}`,
              accent: "bg-amber-500"
            }
          ];
        }
        return [];
      }

      const updatedAt = this.formatDate(detail.updated_at || detail.created_at);
      const valueFormatted = this.formatBytes(detail.value || 0);
      const featureLabel = this.formatEmailFeatureLabel(detail.feature_name || "Storage");

      return [
        {
          id: "storage-detail",
          status: featureLabel,
          date: updatedAt,
          size: `${valueFormatted.value} ${valueFormatted.unit}`,
          accent: "bg-amber-500"
        }
      ];
    },
    buildCategoryDetails() {
      return [];
    },
    formatBytes(bytes, forcedUnitIndex = null) {
      const units = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
      let value = Number(bytes) || 0;
      let unitIndex = forcedUnitIndex ?? 0;

      if (forcedUnitIndex === null) {
        while (value >= 1024 && unitIndex < units.length - 1) {
          value /= 1024;
          unitIndex += 1;
        }
      } else {
        const divisor = 1024 ** unitIndex;
        value = divisor > 0 ? value / divisor : value;
      }

      const roundedValue = value >= 10 ? Number(value.toFixed(0)) : Number(value.toFixed(2));

      return {
        value: roundedValue,
        unit: units[unitIndex],
        unitIndex
      };
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleString();
    },
    formatEmailFeatureLabel(featureName) {
      if (!featureName) {
        return "-";
      }
      const label = featureName.toString().replace(/[_-]/g, " ");
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    closeDrawer() {
      this.$emit('close');
    }
  }
};
</script>
<style scoped>
.badge-dot {
  top: -17px;
  border-radius: 3px;
  left: 20px;
}
</style>